import React from "react";
import { useTranslation } from "react-i18next";

import { notify } from "@/utils";
import { Logger } from "@/utils/logger";
import { ROLE_NAME } from "@/constants/onboarding";

import { useWorkerOnboarding } from "../-worker-onboarding-context";

interface Props {
  onContinue?: (handler: () => Promise<boolean>) => void;
}

function formatMonth(s: string | undefined, locale: string): string {
  if (!s) return "";
  const [y, m] = s.split("-");
  if (!y || !m) return s;
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleString(locale, { month: "short", year: "numeric" });
}

export function WorkerReviewStep({ onContinue }: Props) {
  const { t, i18n } = useTranslation("onboarding");
  const { data, saveDraft } = useWorkerOnboarding();

  React.useEffect(() => {
    if (!onContinue) return;
    onContinue(async () => {
      try {
        await saveDraft();
        return true;
      } catch (e) {
        Logger.error("worker onboarding save failed at review", {
          error: e instanceof Error ? e.message : String(e),
        });
        notify.error(
          e instanceof Error ? e.message : t("workerReview.saveFailed"),
        );
        return false;
      }
    });
  }, [onContinue, saveDraft, t]);

  return (
    <section className="w-full flex flex-col gap-5">
      <div>
        <h2 className="heading-1">{t("workerReview.title")}</h2>
        <p className="body-small text-gray-600 mt-2">
          {t("workerReview.description")}
        </p>
      </div>
      <dl className="flex flex-col gap-4">
        <div>
          <dt className="body-small text-gray-500">{t("workerReview.name")}</dt>
          <dd className="font-semibold">{data.name || "—"}</dd>
        </div>
        <div>
          <dt className="body-small text-gray-500">{t("workerReview.email")}</dt>
          <dd className="font-semibold">{data.email || "—"}</dd>
        </div>
        <div>
          <dt className="body-small text-gray-500">{t("workerReview.location")}</dt>
          <dd className="font-semibold">
            {data.city || "—"}
            {data.city
              ? ` · ${data.radius} ${t("workerReview.miles")}`
              : ""}
          </dd>
        </div>
        <div>
          <dt className="body-small text-gray-500">{t("workerReview.experience")}</dt>
          <dd className="flex flex-col gap-3 mt-1">
            {data.noExperience ? (
              <span className="font-semibold">{t("workerReview.newToRestaurants")}</span>
            ) : data.workExperiences.length === 0 ? (
              "—"
            ) : (
              data.workExperiences.map((we, i) => (
                <div
                  key={i}
                  className="rounded-lg ring-1 ring-garnet/20 p-3 flex flex-col gap-2"
                >
                  <div className="font-semibold">
                    {we.restaurant || "—"}
                    <span className="font-normal text-gray-500">
                      {" · "}
                      {formatMonth(we.start, i18n.language)} -{" "}
                      {we.current
                        ? t("workerReview.present")
                        : formatMonth(we.end, i18n.language)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {we.roles.map((k) => (
                      <span
                        key={k}
                        className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm bg-garnet/10 text-garnet"
                      >
                        <span aria-hidden>{ROLE_NAME[k].icon}</span>
                        {ROLE_NAME[k].label}
                      </span>
                    ))}
                  </div>
                </div>
              ))
            )}
          </dd>
        </div>
        <div>
          <dt className="body-small text-gray-500">{t("workerReview.photo")}</dt>
          <dd className="font-semibold">
            {data.photoUrl ? t("workerReview.uploaded") : "—"}
          </dd>
        </div>
        <div>
          <dt className="body-small text-gray-500">{t("workerReview.resume")}</dt>
          <dd className="font-semibold">
            {data.resumeUrl ? t("workerReview.uploaded") : "—"}
          </dd>
        </div>
      </dl>
    </section>
  );
}
